import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import useCartStore from '../store/cartStore';
import { toast } from 'sonner';
import { CreditCard, Tag, X, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Checkout = () => {
  const navigate = useNavigate();
  const { items, getTotal, clearCart } = useCartStore();
  const [loading, setLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('razorpay');
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: ''
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setCouponLoading(true);
    try {
      const response = await axios.post(`${API}/coupons/validate`, {
        code: couponCode.trim(),
        cart_total: getTotal(),
        cart_items: items.map(item => ({
          product_id: item.product.id,
          category: item.product.category,
          price: item.product.discount_price || item.product.price,
          quantity: item.quantity
        }))
      });
      
      setAppliedCoupon({
        code: response.data.coupon.code,
        discount: response.data.discount_amount,
        type: response.data.coupon.type
      });
      toast.success(response.data.message);
    } catch (error) {
      console.error('Coupon error:', error);
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode('');
    toast.info('Coupon removed');
  };

  const getFinalTotal = () => {
    const subtotal = getTotal();
    if (appliedCoupon) {
      return Math.max(0, subtotal - appliedCoupon.discount);
    }
    return subtotal;
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || !formData.customer_email || !formData.customer_phone || !formData.shipping_address) {
      toast.error('Please fill all fields');
      return;
    }

    if (items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    // Check if any item is out of stock
    const outOfStockItems = items.filter(item => {
      if (item.variantId && item.product.variants) {
        const variant = item.product.variants.find(v => v.id === item.variantId);
        return variant && variant.stock === 0;
      }
      return !item.product.in_stock;
    });

    if (outOfStockItems.length > 0) {
      toast.error('Some items in your cart are out of stock. Please remove them to continue.');
      return;
    }

    setLoading(true);

    try {
      // Get user ID if logged in
      const token = localStorage.getItem('token');
      let userId = null;
      if (token) {
        try {
          const userStr = localStorage.getItem('user');
          if (userStr) {
            const user = JSON.parse(userStr);
            userId = user.id;
          }
        } catch (e) {
          console.log('User not logged in');
        }
      }

      const orderData = {
        ...formData,
        user_id: userId,
        items: items.map(item => ({
          product_id: item.product.id,
          product_name: item.product.name,
          size: item.size,
          color: item.color || 'Standard',
          variant_id: item.variantId || null,
          quantity: item.quantity,
          price: item.product.discount_price || item.product.price
        })),
        subtotal: getTotal(),
        discount_amount: appliedCoupon?.discount || 0,
        coupon_code: appliedCoupon?.code || null,
        total_amount: getFinalTotal(),
        payment_method: paymentMethod
      };

      const response = await axios.post(`${API}/orders/create`, orderData);
      
      if (paymentMethod === 'razorpay') {
        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
          toast.error('Failed to load payment gateway');
          setLoading(false);
          return;
        }

        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount: response.data.total_amount * 100,
          currency: 'INR',
          name: 'JAT Fashion House',
          description: `Order: ${response.data.order_number}`,
          order_id: response.data.razorpay_order_id,
          handler: async (razorpayResponse) => {
            try {
              await axios.post(`${API}/orders/verify-payment`, {
                razorpay_order_id: razorpayResponse.razorpay_order_id,
                razorpay_payment_id: razorpayResponse.razorpay_payment_id,
                razorpay_signature: razorpayResponse.razorpay_signature
              });
              clearCart();
              toast.success('Payment successful! Order confirmed.');
              navigate(`/order-success/${response.data.id}`);
            } catch (error) {
              console.error('Payment verification failed:', error);
              toast.error('Payment verification failed. Please contact support.');
            }
          },
          prefill: {
            name: formData.customer_name,
            email: formData.customer_email,
            contact: formData.customer_phone
          },
          notes: {
            order_number: response.data.order_number,
            shipping_address: formData.shipping_address
          },
          theme: {
            color: '#C8A46A'
          },
          modal: {
            ondismiss: () => {
              toast.info('Payment cancelled. Your order is saved as pending.');
              setLoading(false);
            }
          }
        };

        const razorpay = new window.Razorpay(options);
        razorpay.on('payment.failed', (response) => {
          console.error('Payment failed:', response.error);
          toast.error(`Payment failed: ${response.error.description || 'Please try again.'}`);
          setLoading(false);
        });
        razorpay.open();
        setLoading(false);
      } else {
        clearCart();
        toast.success('Order placed successfully!');
        navigate(`/order-success/${response.data.id}`);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="checkout-page">
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12">
        <h1 className="heading-font text-5xl font-black uppercase mb-8" data-testid="checkout-title">
          <span className="text-[gold]">CHECKOUT</span>
        </h1>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="glass p-6" data-testid="customer-details-form">
                <h2 className="heading-font text-2xl font-bold uppercase mb-6">Customer Details</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-bold mb-2 uppercase text-[charcoal-light]">Full Name *</label>
                    <input
                      type="text"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleInputChange}
                      className="w-full bg-[#121212] border border-white/10 text-white focus:border-[gold] focus:ring-1 focus:ring-[gold] h-12 px-4 outline-none"
                      required
                      data-testid="input-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2 uppercase text-[charcoal-light]">Email *</label>
                    <input
                      type="email"
                      name="customer_email"
                      value={formData.customer_email}
                      onChange={handleInputChange}
                      className="w-full bg-[#121212] border border-white/10 text-white focus:border-[gold] focus:ring-1 focus:ring-[gold] h-12 px-4 outline-none"
                      required
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2 uppercase text-[charcoal-light]">Phone Number *</label>
                    <input
                      type="tel"
                      name="customer_phone"
                      value={formData.customer_phone}
                      onChange={handleInputChange}
                      className="w-full bg-[#121212] border border-white/10 text-white focus:border-[gold] focus:ring-1 focus:ring-[gold] h-12 px-4 outline-none"
                      required
                      data-testid="input-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-bold mb-2 uppercase text-[charcoal-light]">Shipping Address *</label>
                    <textarea
                      name="shipping_address"
                      value={formData.shipping_address}
                      onChange={handleInputChange}
                      rows="4"
                      className="w-full bg-[#121212] border border-white/10 text-white focus:border-[gold] focus:ring-1 focus:ring-[gold] p-4 outline-none"
                      required
                      data-testid="input-address"
                    />
                  </div>
                </div>
              </div>

              <div className="glass p-6" data-testid="payment-method-section">
                <h2 className="heading-font text-2xl font-bold uppercase mb-6">Payment Method</h2>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3 p-4 border border-[gold]/50 bg-[gold]/5 cursor-pointer" data-testid="payment-razorpay">
                    <input
                      type="radio"
                      name="payment_method"
                      value="razorpay"
                      checked={paymentMethod === 'razorpay'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      className="w-5 h-5"
                    />
                    <CreditCard className="w-6 h-6 text-[gold]" />
                    <div>
                      <div className="font-bold">Online Payment (Razorpay)</div>
                      <div className="text-sm text-[charcoal-light]">Pay securely with card, UPI, or net banking</div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="glass p-6 sticky top-24" data-testid="order-summary">
                <h2 className="heading-font text-2xl font-bold uppercase mb-6">Order Summary</h2>
                <div className="space-y-3 mb-6">
                  {items.map((item, index) => (
                    <div key={index} className="flex justify-between text-sm" data-testid={`summary-item-${index}`}>
                      <span className="text-[charcoal-light]">{item.product.name} x {item.quantity}</span>
                      <span className="font-bold">₹{((item.product.discount_price || item.product.price) * item.quantity).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="border-t border-white/10 pt-3 flex justify-between">
                    <span className="text-[charcoal-light]">Subtotal</span>
                    <span className="font-bold">₹{getTotal().toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[charcoal-light]">Shipping</span>
                    <span className="font-bold text-[#10B981]">FREE</span>
                  </div>
                  
                  {/* Coupon Section */}
                  <div className="border-t border-white/10 pt-3">
                    {appliedCoupon ? (
                      <div className="bg-green-900/20 border border-green-500/30 p-3 mb-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <CheckCircle className="w-4 h-4 text-green-500" />
                            <span className="text-green-400 font-mono text-sm">{appliedCoupon.code}</span>
                          </div>
                          <button
                            type="button"
                            onClick={removeCoupon}
                            className="text-red-400 hover:text-red-300"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-sm text-green-400 mt-1">-₹{appliedCoupon.discount.toFixed(2)} discount applied</p>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[charcoal-light]" />
                          <input
                            type="text"
                            placeholder="Coupon code"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                            className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 text-white placeholder-[charcoal-light] focus:border-[gold] focus:outline-none font-mono text-sm"
                            data-testid="coupon-input"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleApplyCoupon}
                          disabled={couponLoading}
                          className="px-4 py-2 bg-[gold] text-white text-sm hover:bg-[gold]/80 disabled:opacity-50"
                          data-testid="apply-coupon-btn"
                        >
                          {couponLoading ? '...' : 'Apply'}
                        </button>
                      </div>
                    )}
                  </div>

                  {appliedCoupon && (
                    <div className="flex justify-between text-green-400">
                      <span>Discount</span>
                      <span>-₹{appliedCoupon.discount.toFixed(2)}</span>
                    </div>
                  )}
                  
                  <div className="border-t border-white/10 pt-3 flex justify-between">
                    <span className="heading-font text-xl font-bold uppercase">Total</span>
                    <span className="heading-font text-2xl font-bold text-[gold]" data-testid="checkout-total">
                      ₹{getFinalTotal().toFixed(2)}
                    </span>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-gold w-full py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="place-order-button"
                >
                  {loading ? 'PROCESSING...' : 'PLACE ORDER'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;